import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Title, TextInput, Group, Button, Modal, Stack, Paper, Text, Badge, Menu, ActionIcon, Checkbox, PasswordInput, NumberInput, Divider, Tabs } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSearch, IconPlus, IconDots, IconEdit, IconTrash, IconUsers, IconUserCheck, IconLock, IconBrandTelegram, IconRefresh, IconRestore, IconTrashX, IconCheck } from '@tabler/icons-react';
import { formatDate } from '../../utils/date';
import { usersApi, groupsApi, telegramApi } from '../../api/client';
import { PageContainer } from '../../components/PageContainer';
import { User } from '../../types/user';
import { Group as GroupType } from '../../types/group';
import { PaginatedList } from '../../components/List';
import { useQueryClient } from '@tanstack/react-query';

export function AdminUsersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<string | null>('active');
  const [search, setSearch] = useState('');
  const [groups, setGroups] = useState<GroupType[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showGroupsModal, setShowGroupsModal] = useState(false);

  const loadGroups = useCallback(async () => {
    try {
      const res = await groupsApi.list();
      setGroups(res.data?.data || []);
    } catch (err) {
      console.error('Failed to load groups:', err);
    }
  }, []);

  const refreshUsers = () => {
    queryClient.invalidateQueries({ queryKey: ['users'] });
    queryClient.invalidateQueries({ queryKey: ['users-deleted'] });
  };

  const selectUserForGroups = async (user: User) => {
    setSelectedUser(user);
    await loadGroups();
    try {
      const res = await usersApi.getGroups(user.id);
      setUserGroups(res.data?.data?.groups || []);
      setShowGroupsModal(true);
    } catch (err) {
      console.error('Failed to load user groups:', err);
    }
  };

  const handleDelete = async (user: User) => {
    if (!confirm(t('users.delete_confirm', { login: user.login }))) return;
    try {
      await usersApi.delete(user.id);
      refreshUsers();
      notifications.show({ title: t('common.success'), message: t('users.deleted_success'), color: 'green' });
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('users.delete_failed'), color: 'red' });
    }
  };

  const handleRestore = async (user: User) => {
    if (!confirm(t('users.restore_confirm', { login: user.login }))) return;
    try {
      await usersApi.restore(user.id);
      refreshUsers();
      notifications.show({ title: t('common.success'), message: t('users.restored_success'), color: 'green' });
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('users.restore_failed'), color: 'red' });
    }
  };

  const handleHardDelete = async (user: User) => {
    if (!confirm(t('users.hard_delete_confirm', { login: user.login }))) return;
    try {
      await usersApi.hardDelete(user.id);
      refreshUsers();
      notifications.show({ title: t('common.success'), message: t('users.hard_deleted_success'), color: 'green' });
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('users.hard_delete_failed'), color: 'red' });
    }
  };

  const handleStatusChange = async (userId: string, newStatus: string) => {
    try {
      await usersApi.updateStatus(userId, newStatus);
      refreshUsers();
      notifications.show({ title: t('common.success'), message: t('users.status_updated'), color: 'green' });
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('users.status_update_failed'), color: 'red' });
    }
  };

  const handleToggleGroup = async (groupId: string, inGroup: boolean) => {
    if (!selectedUser) return;
    try {
      if (inGroup) {
        await groupsApi.removeUser(groupId, selectedUser.id);
      } else {
        await groupsApi.addUser(groupId, selectedUser.id);
      }
      const res = await usersApi.getGroups(selectedUser.id);
      setUserGroups(res.data?.data?.groups || []);
      notifications.show({ title: t('common.success'), message: t('users.groups_updated'), color: 'green' });
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('users.groups_update_failed'), color: 'red' });
    }
  };

  return (
    <PageContainer>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={2}>{t('users.title')}</Title>
          <ActionIcon variant="filled" color="blue" size="lg" onClick={() => setShowCreateModal(true)} title={t('users.create_user')}>
            <IconPlus size={20} />
          </ActionIcon>
        </Group>

        <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="active">{t('users.status_active')}</Tabs.Tab>
            <Tabs.Tab value="deleted">{t('users.deleted_users')}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="active" pt="md" style={{ minWidth: 0 }}>
            <Stack gap="md">
              <TextInput
                placeholder={t('users.search_placeholder')}
                leftSection={<IconSearch size={16} />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <PaginatedList<User>
                queryKey={['users', { search }]}
                fetchFn={async (offset, limit) => {
                  const res = await usersApi.list({ search: search || undefined, limit, offset });
                  const data = res.data?.data?.data || res.data?.data || [];
                  const total = res.data?.data?.pagination?.total || res.data?.pagination?.total || data.length;
                  return { data: Array.isArray(data) ? data : [], total };
                }}
                renderItem={(user) => (
                  <UserCard
                    user={user}
                    onClick={(u) => navigate(`/users/${u.id}`)}
                    onEdit={(u) => { setSelectedUser(u); setShowEditModal(true); }}
                    onDelete={handleDelete}
                    onGroups={selectUserForGroups}
                    onStatusChange={handleStatusChange}
                  />
                )}
                getItemKey={(user) => user.id}
                emptyMessage={t('users.not_found')}
                pageSize={20}
                showPageInfo
                showPageSizeSelector
              />
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="deleted" pt="md" style={{ minWidth: 0 }}>
            <PaginatedList<User>
              queryKey={['users-deleted']}
              fetchFn={async (offset, limit) => {
                const res = await usersApi.listDeleted({ limit, offset });
                const data = res.data?.data?.data || res.data?.data || [];
                const total = res.data?.data?.pagination?.total || res.data?.pagination?.total || data.length;
                return { data: Array.isArray(data) ? data : [], total };
              }}
              renderItem={(user) => (
                <DeletedUserCard user={user} onRestore={handleRestore} onHardDelete={handleHardDelete} />
              )}
              getItemKey={(user) => user.id}
              emptyMessage={t('users.not_found')}
              pageSize={20}
              showPageInfo
              showPageSizeSelector
            />
          </Tabs.Panel>
        </Tabs>
      </Stack>

      <Modal opened={showCreateModal} onClose={() => setShowCreateModal(false)} title={t('users.create_user')} size="md">
        <CreateUserForm onSuccess={() => { refreshUsers(); setShowCreateModal(false); }} />
      </Modal>

      <Modal opened={showEditModal} onClose={() => setShowEditModal(false)} title={t('users.edit_user')} size="md">
        {selectedUser && <EditUserForm user={selectedUser} onSuccess={() => { refreshUsers(); setShowEditModal(false); }} />}
      </Modal>

      <Modal opened={showGroupsModal} onClose={() => setShowGroupsModal(false)} title={`${t('users.groups')}: ${selectedUser?.login}`}>
        <Stack>
          {groups.map(group => {
            const inGroup = userGroups.includes(group.id);
            return (
              <Checkbox
                key={group.id}
                label={group.name}
                description={group.description}
                checked={inGroup}
                onChange={() => handleToggleGroup(group.id, inGroup)}
              />
            );
          })}
        </Stack>
      </Modal>
    </PageContainer>
  );
}

interface DeletedUserCardProps {
  user: User;
  onRestore: (user: User) => void;
  onHardDelete: (user: User) => void;
}

function DeletedUserCard({ user, onRestore, onHardDelete }: DeletedUserCardProps) {
  const { t } = useTranslation();

  return (
    <Paper p="md" withBorder style={{ opacity: 0.7 }}>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Text fw={600} style={{ fontSize: 'var(--font-size-sm)' }}>{user.login}</Text>
          <Text c="dimmed" truncate style={{ fontSize: 'var(--font-size-xs)' }}>{user.email || t('users.no_email')}</Text>
          {user.first_name && <Text c="dimmed" truncate style={{ fontSize: 'var(--font-size-xs)' }}>{user.first_name} {user.last_name}</Text>}
          <Text c="dimmed" style={{ fontSize: 'var(--font-size-xs)' }}>{t('users.created')}: {formatDate(user.created_at)}</Text>
          <Badge color="red" style={{ alignSelf: 'flex-start', padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>{t('common.delete')}</Badge>
        </Stack>
        <Group gap="xs">
          <ActionIcon variant="light" color="green" onClick={() => onRestore(user)} title={t('users.restore')}>
            <IconRestore size={16} />
          </ActionIcon>
          <ActionIcon variant="light" color="red" onClick={() => onHardDelete(user)} title={t('users.hard_delete')}>
            <IconTrashX size={16} />
          </ActionIcon>
        </Group>
      </Group>
    </Paper>
  );
}

interface UserCardProps {
  user: User;
  onClick: (user: User) => void;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onGroups: (user: User) => void;
  onStatusChange: (userId: string, status: string) => void;
}

function UserCard({ user, onClick, onEdit, onDelete, onGroups, onStatusChange }: UserCardProps) {
  const { t } = useTranslation();
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'pending': return 'yellow';
      case 'blocked': return 'red';
      default: return 'gray';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return t('users.status_active');
      case 'pending': return t('users.status_pending');
      case 'blocked': return t('users.status_blocked');
      default: return status;
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-menu-trigger]') || 
        (e.target as HTMLElement).closest('.mantine-Menu-dropdown')) {
      return;
    }
    onClick(user);
  };

  return (
    <Paper p="md" withBorder style={{ cursor: 'pointer', transition: 'box-shadow 0.2s' }}
      onClick={handleCardClick}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--mantine-shadow-sm)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ''; }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Text fw={600} style={{ fontSize: 'var(--font-size-sm)' }}>{user.login}</Text>
          <Text c="dimmed" truncate style={{ fontSize: 'var(--font-size-xs)' }}>{user.email || t('users.no_email')}</Text>
          {user.first_name && <Text c="dimmed" truncate style={{ fontSize: 'var(--font-size-xs)' }}>{user.first_name} {user.last_name}</Text>}
          <Text c="dimmed" style={{ fontSize: 'var(--font-size-xs)' }}>{t('users.created')}: {formatDate(user.created_at)}</Text>
          <Badge color={getStatusColor(user.status)} style={{ alignSelf: 'flex-start', padding: 'var(--badge-padding)', fontSize: 'var(--font-size-xs)' }}>{getStatusLabel(user.status)}</Badge>
        </Stack>
        <Menu shadow="md" width={200} position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="subtle" size="sm" onClick={(e) => e.stopPropagation()}>
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<IconEdit size={14} />} onClick={() => onEdit(user)}>{t('common.edit')}</Menu.Item>
            <Menu.Item leftSection={<IconUsers size={14} />} onClick={() => onGroups(user)}>{t('users.groups')}</Menu.Item>
            <Menu.Divider />
            <Menu.Label>{t('users.status')}</Menu.Label>
            <Menu.Item leftSection={<IconUserCheck size={14} />} onClick={() => onStatusChange(user.id, 'active')} disabled={user.status === 'active'}>{t('users.activate')}</Menu.Item>
            <Menu.Item leftSection={<IconLock size={14} />} onClick={() => onStatusChange(user.id, 'blocked')} disabled={user.status === 'blocked'}>{t('users.block')}</Menu.Item>
            <Menu.Divider />
            <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => onDelete(user)}>{t('common.delete')}</Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Paper>
  );
}

function CreateUserForm({ onSuccess }: { onSuccess: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ 
    login: '', password: '', first_name: '', last_name: '', email: '',
    telegram_id: null as number | null, telegram_username: ''
  });
  const [loading, setLoading] = useState(false);
  const [fetchingTg, setFetchingTg] = useState(false);

  const handleFetchTelegram = async () => {
    if (!form.telegram_id) return;
    setFetchingTg(true);
    try {
      const res = await telegramApi.getUser(form.telegram_id);
      const tgUser = res.data?.data;
      if (tgUser) {
        setForm(prev => ({
          ...prev,
          telegram_username: tgUser.username || '',
          first_name: prev.first_name || tgUser.first_name || '',
          last_name: prev.last_name || tgUser.last_name || ''
        }));
        notifications.show({ title: t('common.success'), message: t('users.telegram_fetched'), color: 'green' });
      }
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('users.telegram_fetch_failed'), color: 'red' });
    } finally {
      setFetchingTg(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Frontend validation
    if (form.login.length < 3) {
      notifications.show({ title: t('common.error'), message: t('users.login_too_short'), color: 'red' });
      return;
    }
    if (form.password && form.password.length < 6) {
      notifications.show({ title: t('common.error'), message: t('users.password_too_short'), color: 'red' });
      return;
    }
    
    setLoading(true);
    try {
      await usersApi.create({
        login: form.login,
        password: form.password || undefined,
        first_name: form.first_name || undefined,
        last_name: form.last_name || undefined,
        email: form.email || undefined,
        telegram_id: form.telegram_id || undefined,
        telegram_username: form.telegram_username || undefined
      });
      notifications.show({ title: t('common.success'), message: t('users.created_success'), color: 'green' });
      onSuccess();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: { message?: string } } } };
      let msg = axiosErr?.response?.data?.error?.message || t('users.create_failed');
      // Translate common backend errors
      if (msg.includes('already exists') || msg.includes('duplicate')) {
        msg = t('users.login_exists');
      } else if (msg.includes('Password must be at least')) {
        msg = t('users.password_too_short');
      } else if (msg.includes('Login must be at least')) {
        msg = t('users.login_too_short');
      }
      notifications.show({ title: t('common.error'), message: msg, color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack>
        <TextInput label={t('users.login')} value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })} required />
        <PasswordInput label={t('users.password')} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <TextInput label={t('users.first_name')} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
        <TextInput label={t('users.last_name')} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
        <TextInput label={t('users.email')} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Divider label={<Group gap="xs"><IconBrandTelegram size={16} /><Text style={{ fontSize: 'var(--font-size-sm)' }}>{t('common.telegram')}</Text></Group>} />
        <Group grow align="flex-end">
          <NumberInput label={t('users.telegram_id')} value={form.telegram_id || ''} onChange={(val) => setForm({ ...form, telegram_id: typeof val === 'number' ? val : null })} hideControls placeholder="123456789" />
          <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={handleFetchTelegram} loading={fetchingTg} disabled={!form.telegram_id}>{t('users.fetch_telegram')}</Button>
        </Group>
        <TextInput label={t('users.telegram_username')} value={form.telegram_username} onChange={(e) => setForm({ ...form, telegram_username: e.target.value })} placeholder="username" leftSection={<Text c="dimmed" style={{ fontSize: 'var(--font-size-sm)' }}>@</Text>} />
        <Group justify="flex-end">
          <ActionIcon type="submit" variant="filled" color="green" size="lg" loading={loading} disabled={!form.login} title={t('common.create')}>
            <IconCheck size={20} />
          </ActionIcon>
        </Group>
      </Stack>
    </form>
  );
}

function EditUserForm({ user, onSuccess }: { user: User; onSuccess: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ first_name: user.first_name || '', last_name: user.last_name || '', email: user.email || '', phone: user.phone || '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await usersApi.update(user.id, form);
      notifications.show({ title: t('common.success'), message: t('users.updated_success'), color: 'green' });
      onSuccess();
    } catch (err) {
      notifications.show({ title: t('common.error'), message: t('users.update_failed'), color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack>
        <TextInput label={t('users.first_name')} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
        <TextInput label={t('users.last_name')} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
        <TextInput label={t('users.email')} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <TextInput label={t('users.phone')} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        <Group justify="flex-end">
          <ActionIcon type="submit" variant="filled" color="green" size="lg" loading={loading} title={t('common.save')}>
            <IconCheck size={20} />
          </ActionIcon>
        </Group>
      </Stack>
    </form>
  );
}

export default AdminUsersPage;
